import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Checkbox, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { useLocalStorageByUser } from 'common/hooks';
import { SearchContainer, StyledTextField } from './ExpandableSearch.styles';

export interface ExpandableSearchHandle {
  expand: () => void;
}

interface ExpandableSearchProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  isValuationsPage?: boolean;
  /** When provided with onSearchAllChange, parent controls "search all" (for API). */
  searchAll?: boolean;
  onSearchAllChange?: (value: boolean) => void;
}

export const ExpandableSearch = forwardRef<ExpandableSearchHandle, ExpandableSearchProps>(({
  value,
  onChange,
  placeholder = 'Search...',
  isValuationsPage = false,
  searchAll: searchAllProp,
  onSearchAllChange,
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchAllLocal, setSearchAllLocal, isSearchAllReady] = useLocalStorageByUser(
    'searchAll',
    false
  );
  const isControlled = searchAllProp !== undefined && onSearchAllChange !== undefined;
  const isSearchAllPreferenceReady = isControlled || isSearchAllReady;
  const searchAll = isControlled ? searchAllProp : searchAllLocal;
  const setSearchAll = isControlled ? onSearchAllChange! : setSearchAllLocal;
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleExpand = () => {
    setIsExpanded(true);
    // Focus input after expansion animation starts
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  useImperativeHandle(ref, () => ({
    expand: handleExpand,
  }));

  const handleClear = () => {
    // Create synthetic event for onChange
    const syntheticEvent = {
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
    inputRef.current?.focus();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!value) {
      const relatedTarget = e.relatedTarget as Node | null;
      if (!containerRef.current?.contains(relatedTarget)) {
        setIsExpanded(false);
      }
    }
  };

  return (
    <SearchContainer isExpanded={isExpanded}>
      {isExpanded ? (
        <Box ref={containerRef} display="flex" width="100%" alignItems="center">
          <StyledTextField
            size="small"
            variant="outlined"
            inputRef={inputRef}
            value={value}
            onChange={onChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            fullWidth
            sx={theme => ({
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.neutral[200],
                color: theme.palette.neutral[800],
                height: '24px',
                fontSize: theme.typography.caption.fontSize,
                '& fieldset': {
                  borderColor: theme.palette.neutral[500],
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.neutral[500],
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.brand[400],
                },
              },
              '& .MuiInputBase-input': {
                padding: '0 8px',
              },
            })}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={theme => ({ color: theme.palette.neutral[800] })} />
                  </InputAdornment>
                ),
                endAdornment: value ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      onMouseDown={e => e.preventDefault()}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          {isValuationsPage && (
            <Tooltip title="Search All Valuations" arrow>
              <Checkbox
                checked={searchAll}
                disabled={!isSearchAllPreferenceReady}
                sx={{ visibility: isSearchAllPreferenceReady ? 'visible' : 'hidden' }}
                onChange={() => setSearchAll(!searchAll)}
                inputProps={{ 'aria-label': 'controlled', 'aria-describedby': 'Full Search Toggle' }}
              />
            </Tooltip>
          )}
        </Box>
      ) : (
        <Tooltip title="Search by company name" arrow>
          <IconButton
            onClick={handleExpand}
            size="small"
            sx={theme => ({
              backgroundColor: theme.palette.navigation.tertiary.iconButton.background,
              borderRadius: `${theme.shape.radius.xs}px`,
              color: theme.palette.navigation.tertiary.iconButton.color,
              height: 24,
              padding: 0,
              width: 24,
              '&:hover': {
                backgroundColor: theme.palette.navigation.tertiary.iconButton.hoverBackground,
                color: theme.palette.navigation.tertiary.iconButton.hoverColor,
              },
            })}>
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </SearchContainer>
  );
});
